'use client';

import { createContext, useCallback, useContext, type ReactNode } from 'react';

export type OnTerminalAlwaysAllow = (pattern: string) => void;

type AlwaysAllowSource = { allowRulePattern?: string } | undefined;

export type BuildTerminalAlwaysAllowHandler = (
  actionRequest: AlwaysAllowSource,
  approve: () => void,
) => (() => void) | undefined;

const TerminalApprovalRulesContext = createContext<
  OnTerminalAlwaysAllow | undefined
>(undefined);

export function TerminalApprovalRulesProvider({
  onAlwaysAllow,
  children,
}: {
  onAlwaysAllow?: OnTerminalAlwaysAllow;
  children: ReactNode;
}) {
  return (
    <TerminalApprovalRulesContext.Provider value={onAlwaysAllow}>
      {children}
    </TerminalApprovalRulesContext.Provider>
  );
}

export function useTerminalAlwaysAllow(): OnTerminalAlwaysAllow | undefined {
  return useContext(TerminalApprovalRulesContext);
}

export function useTerminalAlwaysAllowHandler(): BuildTerminalAlwaysAllowHandler {
  const onAlwaysAllow = useTerminalAlwaysAllow();

  return useCallback<BuildTerminalAlwaysAllowHandler>(
    (actionRequest, approve) => {
      const pattern = actionRequest?.allowRulePattern;

      if (!onAlwaysAllow || !pattern) {
        return undefined;
      }

      return () => {
        onAlwaysAllow(pattern);
        approve();
      };
    },
    [onAlwaysAllow],
  );
}
